/*
 * SPDX-FileCopyrightText: © 2024 Schneider Electric
 *
 * SPDX-License-Identifier: Apache-2.0
 */

@import url('../shared.css');

:host {
  display: block;
}

.qds-disabled {
  opacity: var(--qds-theme-disabled);
  pointer-events: none;
}

.qds-list-item,
.qds-indented-content {
  align-items: center;
  box-sizing: border-box;
  display: flex;
}

.qds-list-action {
  pointer-events: none;
}

.qds-indented-content {
  flex: 1 0;
  align-self: stretch;
}

.qds-destination {
  transform: scaleX(var(--qds-direction-factor, 1));
}

.qds-divider {
  border-block-end: var(--qds-list-border-width) solid
    var(--qds-theme-divider-standard);
}

.qds-icon {
  color: var(--qds-theme-text-standard);
}

.qds-texts {
  justify-items: center;
  display: flex;
  flex: 1 0;
  flex-direction: column;
  padding: 0;
  position: relative;
}

.qds-input {
  appearance: none;
  background: none;
  border: none;
  border-radius: var(--qds-control-border-radius);
  inset: 0;
  margin: 0;
  outline: none;
  position: absolute;
}

.qds-title {
  color: var(--qds-theme-title);
}

.qds-subtitle {
  color: var(--qds-theme-subtitle);
}

.qds-kicker {
  color: var(--qds-theme-kicker);
}

[data-size='small'] {
  &.qds-list-item {
    gap: var(--qds-list-item-small-gap-internal);
    padding-inline-start: var(--qds-list-item-small-padding-horizontal);
  }

  .qds-indented-content {
    gap: var(--qds-list-item-small-gap-internal);
    min-height: var(--qds-list-item-small-height);
    padding-block: var(--qds-list-item-small-padding-vertical);
    padding-inline-end: var(--qds-list-item-small-padding-horizontal);
  }

  .qds-icon {
    font-size: var(--qds-control-small-icon-size);
  }

  .qds-main-icon {
    font-size: var(--qds-list-item-small-icon-size);
  }

  .qds-texts {
    gap: var(--qds-list-item-small-titles-gap);
  }

  .qds-kicker {
    font: var(--qds-list-item-small-kicker);
  }

  .qds-title {
    font: var(--qds-list-item-small-title);
  }

  .qds-subtitle {
    font: var(--qds-list-item-small-subtitle);
  }
}

[data-size='standard'] {
  &.qds-list-item {
    gap: var(--qds-list-item-standard-gap-internal);
    padding-inline-start: var(--qds-list-item-standard-padding-horizontal);
  }

  .qds-indented-content {
    gap: var(--qds-list-item-standard-gap-internal);
    min-height: var(--qds-list-item-standard-height);
    padding-block: var(--qds-list-item-standard-padding-vertical);
    padding-inline-end: var(--qds-list-item-standard-padding-horizontal);
  }

  .qds-icon {
    font-size: var(--qds-control-standard-icon-size);
  }

  .qds-main-icon {
    font-size: var(--qds-list-item-standard-icon-size);
  }

  .qds-texts {
    gap: var(--qds-list-item-standard-titles-gap);
  }

  .qds-kicker {
    font: var(--qds-list-item-standard-kicker);
  }

  .qds-title {
    font: var(--qds-list-item-standard-title);
  }

  .qds-subtitle {
    font: var(--qds-list-item-standard-subtitle);
  }
}

[data-size='large'] {
  &.qds-list-item {
    gap: var(--qds-list-item-large-gap-internal);
    padding-inline-start: var(--qds-list-item-large-padding-horizontal);
  }

  .qds-indented-content {
    gap: var(--qds-list-item-large-gap-internal);
    min-height: var(--qds-list-item-large-height);
    padding-block: var(--qds-list-item-large-padding-vertical);
    padding-inline-end: var(--qds-list-item-large-padding-horizontal);
  }

  .qds-icon {
    font-size: var(--qds-control-large-icon-size);
  }

  .qds-main-icon {
    font-size: var(--qds-list-item-large-icon-size);
  }

  .qds-texts {
    gap: var(--qds-list-item-large-titles-gap);
  }

  .qds-kicker {
    font: var(--qds-list-item-large-kicker);
  }

  .qds-title {
    font: var(--qds-list-item-large-title);
  }

  .qds-subtitle {
    font: var(--qds-list-item-large-subtitle);
  }
}

.qds-list-item {
  &:hover {
    background-color: var(--qds-theme-interactive-background-hover);
  }

  &:active {
    background-color: var(--qds-theme-interactive-background-pressed);
  }

  &:has(input:focus-visible) {
    outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
    outline-offset: var(--qds-focus-border-offset);
  }
}

.qds-selected {
  &.qds-list-item {
    background-color: var(--qds-theme-interactive-background-selected);
  }

  &[data-size='small'] .qds-title {
    font: var(--qds-list-item-small-title-emphasized);
  }

  &[data-size='standard'] .qds-title {
    font: var(--qds-list-item-standard-title-emphasized);
  }

  &[data-size='large'] .qds-title {
    font: var(--qds-list-item-large-title-emphasized);
  }
}

:host(:last-of-type) {
  .qds-divider {
    border-block-end: none;
  }
}
